/**
 * The handful of pieces every screen is built from.
 *
 * Nothing here knows about the API. A `Metric` is a number with a label, a `Pill` is a word with
 * a colour, a `Modal` is a box that traps focus. The screens decide what goes in them.
 */

import { useEffect, useId, useState, type ReactNode } from 'react';

import { modeLabel } from './format';

export function PageHead({
  eyebrow,
  title,
  note,
  actions,
}: {
  eyebrow?: string;
  title: string;
  note?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1 className="page-title">{title}</h1>
        {note ? <p className="page-note">{note}</p> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </div>
  );
}

export function Metric({
  label,
  value,
  note,
  tone,
  small,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  tone?: 'warn' | 'bad';
  small?: boolean;
}) {
  return (
    <div className={`card${tone ? ` ${tone}` : ''}`}>
      <div className="card-label">{label}</div>
      <div className={`card-value${small ? ' small' : ''}`}>{value}</div>
      {note ? <div className="card-note">{note}</div> : null}
    </div>
  );
}

export function Panel({
  title,
  eyebrow,
  actions,
  children,
  flush,
}: {
  title?: ReactNode;
  eyebrow?: string;
  actions?: ReactNode;
  children: ReactNode;
  /** Table content supplies its own padding. */
  flush?: boolean;
}) {
  return (
    <section className="panel">
      {title || eyebrow ? (
        <div className="panel-head">
          <div>
            {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
            {title}
          </div>
          {actions}
        </div>
      ) : null}
      {flush ? (
        <div className="table-wrap">{children}</div>
      ) : (
        <div className="panel-body">{children}</div>
      )}
    </section>
  );
}

export function Pill({
  value,
  className,
}: {
  value: string | null | undefined;
  className?: string;
}) {
  if (!value) return <span className="faint">—</span>;
  return (
    <span className={`pill ${value}${className ? ` ${className}` : ''}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

/** Voice, text or night shift, with a glyph so the transcript can be read at a glance. */
export function Mode({ value }: { value: string | null | undefined }) {
  if (!value) return null;
  return (
    <span className={`mode ${value}`} title={modeLabel(value)}>
      <svg className="mode-glyph" viewBox="0 0 16 16" aria-hidden="true">
        {value === 'voice' ? (
          <>
            <rect x="6" y="2" width="4" height="8" rx="2" fill="currentColor" />
            <path
              d="M3.5 8a4.5 4.5 0 0 0 9 0M8 12.5V15"
              stroke="currentColor"
              strokeWidth="1.4"
              fill="none"
            />
          </>
        ) : value === 'night_shift' ? (
          <path d="M10 2a6 6 0 1 0 4 10.5A5 5 0 0 1 10 2z" fill="currentColor" />
        ) : (
          <path d="M2 3h12v8H6l-3 3v-3H2z" fill="currentColor" />
        )}
      </svg>
      {modeLabel(value)}
    </span>
  );
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return <p className="muted">{what}…</p>;
}

export function Failed({ error, fallback }: { error: unknown; fallback: string }) {
  return (
    <div className="notice error" role="alert">
      {error instanceof Error ? error.message : fallback}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="panel-body muted">{children}</div>;
}

export function Tabs<T extends string>({
  tabs,
  current,
  onChange,
}: {
  tabs: { key: T; label: string }[];
  current: T;
  onChange: (key: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          className="tab"
          aria-selected={tab.key === current}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const heading = useId();
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="scrim"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={heading}>
        <h2 id={heading}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/**
 * A yes/no that has to be typed for. Used for closing an account, which cannot be undone from the
 * portal and should never happen because a hand slipped.
 */
export function Confirm({
  title,
  body,
  word,
  verb,
  busy,
  onConfirm,
  onClose,
  danger,
}: {
  title: string;
  body: ReactNode;
  /** What has to be typed before the button enables. Omit for a plain confirmation. */
  word?: string;
  verb: string;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  danger?: boolean;
}) {
  const [typed, setTyped] = useState('');
  const ready = !word || typed.trim() === word;
  return (
    <Modal title={title} onClose={onClose}>
      <div className="stack">{body}</div>
      {word ? (
        <label className="field" style={{ marginTop: 14 }}>
          <span>Type {word} to continue</span>
          <input
            type="text"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoFocus
          />
        </label>
      ) : null}
      <div className="modal-actions">
        <button type="button" className="button-quiet" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={`button${danger ? ' danger' : ''}`}
          disabled={!ready || busy}
          onClick={onConfirm}
        >
          {busy ? 'Working…' : verb}
        </button>
      </div>
    </Modal>
  );
}

/** The invitation token, shown once, with the words that go with it. */
export function TokenReveal({
  email,
  organisation,
  token,
  expires,
  onClose,
}: {
  email: string;
  organisation: string;
  token: string;
  expires: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }
  return (
    <Modal title="Invitation issued" onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        Send this token to <strong className="good">{email}</strong> for {organisation}. It is shown
        once — the API keeps only a digest — and it expires {expires}.
      </p>
      <code className="token">{token}</code>
      <div className="modal-actions">
        <button type="button" className="button-quiet" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy token'}
        </button>
        <button type="button" className="button" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- charts

/** Stacked bars per day: text, voice, night shift, with failures drawn on top in red. */
export function TurnsChart({
  days,
}: {
  days: { day: string; chat: number; voice: number; night: number; failed: number }[];
}) {
  const width = 600;
  const height = 160;
  const pad = { top: 8, bottom: 18, left: 28, right: 4 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...days.map((d) => d.chat + d.voice + d.night));
  const slot = innerW / Math.max(1, days.length);
  const barW = Math.max(2, slot * 0.66);
  const y = (n: number) => pad.top + innerH - (n / max) * innerH;
  return (
    <svg className="chart" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <line className="axis" x1={pad.left} x2={width - pad.right} y1={y(0)} y2={y(0)} />
      <text x={0} y={pad.top + 8}>
        {max}
      </text>
      <text x={0} y={y(0)}>
        0
      </text>
      {days.map((d, i) => {
        const x = pad.left + i * slot + (slot - barW) / 2;
        const chatTop = y(d.chat);
        const voiceTop = y(d.chat + d.voice);
        const nightTop = y(d.chat + d.voice + d.night);
        const label = i === 0 || i === days.length - 1 || i % Math.ceil(days.length / 6) === 0;
        return (
          <g key={d.day}>
            <rect className="bar" x={x} y={chatTop} width={barW} height={y(0) - chatTop} />
            <rect
              className="bar voice"
              x={x}
              y={voiceTop}
              width={barW}
              height={chatTop - voiceTop}
            />
            <rect
              className="bar night"
              x={x}
              y={nightTop}
              width={barW}
              height={voiceTop - nightTop}
            />
            {d.failed ? (
              <rect
                className="bar failed"
                x={x}
                y={nightTop - 3}
                width={barW}
                height={Math.max(2, (d.failed / max) * innerH)}
              />
            ) : null}
            {label ? (
              <text x={x + barW / 2} y={height - 4} textAnchor="middle">
                {d.day.slice(5)}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

/** One line over days, with the area under it. Money or counts; the caller says which. */
export function LineChart({
  points,
  format,
}: {
  points: { day: string; value: number }[];
  format: (value: number) => string;
}) {
  const width = 600;
  const height = 160;
  const pad = { top: 10, bottom: 18, left: 44, right: 6 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1e-9, ...points.map((p) => p.value));
  const step = innerW / Math.max(1, points.length - 1);
  const x = (i: number) => pad.left + i * step;
  const y = (v: number) => pad.top + innerH - (v / max) * innerH;
  const path = points
    .map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`)
    .join(' ');
  const area = points.length
    ? `${path} L${x(points.length - 1).toFixed(1)},${y(0)} L${x(0)},${y(0)} Z`
    : '';
  return (
    <svg className="chart" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <line className="axis" x1={pad.left} x2={width - pad.right} y1={y(0)} y2={y(0)} />
      <text x={0} y={pad.top + 8}>
        {format(max)}
      </text>
      <text x={0} y={y(0)}>
        {format(0)}
      </text>
      {area ? <path className="area" d={area} /> : null}
      <path className="line" d={path} />
      {points.map((p, i) =>
        i === 0 || i === points.length - 1 || i % Math.ceil(points.length / 6) === 0 ? (
          <text key={p.day} x={x(i)} y={height - 4} textAnchor="middle">
            {p.day.slice(5)}
          </text>
        ) : null,
      )}
    </svg>
  );
}

export function Meter({ part, whole, bad }: { part: number; whole: number; bad?: boolean }) {
  const share = whole ? Math.min(100, Math.round((part / whole) * 100)) : 0;
  return (
    <div className="meter" title={`${share}%`}>
      <i className={bad ? 'bad' : undefined} style={{ width: `${share}%` }} />
    </div>
  );
}
